import { MouseEvent, useEffect, useState } from "react";

import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { OfferType } from "../../models/EnginesWorldwide";
import { IVehicle } from "../../models/Vehicle";
import PriceFormat from "../../utils/priceFormat";
import { ButtonInput } from "../shared/Inputs/ButtonInput";

type EnginesWorldwideLookupProps = {
  vehicle: Pick<IVehicle, "id" | "enginesWorldwideEngineOffer" | "enginesWorldwideVfeOffer" | "enginesWorldwide">;
  variant?: "details" | "inline";
  showLookupButton?: boolean;
  className?: string;
  onUpdated?: (vehicle: IVehicle) => void;
};

const parseOffer = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatOffer = (value?: string | number | null) => {
  const parsed = parseOffer(value);
  return parsed !== null ? PriceFormat(parsed) : "N/A";
};

export default function EnginesWorldwideLookup({
  vehicle,
  variant = "details",
  showLookupButton = true,
  className = "",
  onUpdated,
}: EnginesWorldwideLookupProps) {
  const { enginesWorldwideRefresh } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Engines Worldwide lookup");
  const [engineOffer, setEngineOffer] = useState<string | number | null>(vehicle.enginesWorldwideEngineOffer);
  const [vfeOffer, setVfeOffer] = useState<string | number | null>(vehicle.enginesWorldwideVfeOffer);

  useEffect(() => {
    setEngineOffer(vehicle.enginesWorldwideEngineOffer ?? vehicle.enginesWorldwide?.find((e) => e.type === OfferType.Engine)?.price ?? null);
    setVfeOffer(vehicle.enginesWorldwideVfeOffer ?? vehicle.enginesWorldwide?.find((e) => e.type === OfferType.Vfe)?.price ?? null);
  }, [vehicle.id, vehicle.enginesWorldwideEngineOffer, vehicle.enginesWorldwideVfeOffer, vehicle.enginesWorldwide]);

  const hasLookup = parseOffer(engineOffer) !== null || parseOffer(vfeOffer) !== null || (vehicle.enginesWorldwide?.length ?? 0) > 0;
  const canRunLookup = showLookupButton && !hasLookup;

  const handleLookup = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    enginesWorldwideRefresh.mutate(vehicle.id, {
      onSuccess: (response) => {
        setEngineOffer(response.data.enginesWorldwideEngineOffer);
        setVfeOffer(response.data.enginesWorldwideVfeOffer);
        onUpdated?.(response.data);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  if (variant === "inline") {
    return (
      <div className={`flex flex-wrap items-center gap-3 ${className}`} onClick={(e) => e.stopPropagation()}>
        <span>Engine: {formatOffer(engineOffer)}</span>
        <span>VFE: {formatOffer(vfeOffer)}</span>
        {canRunLookup && (
          <ButtonInput
            label="Lookup EWW"
            onClick={handleLookup}
            classes=""
            disabled={!vehicle.id}
            isLoading={enginesWorldwideRefresh.isLoading}
            size="sm"
          />
        )}
      </div>
    );
  }

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      <div className="py-2 grid grid-cols-3 gap-4">
        <dt className="text-sm font-medium text-red-500">Engine Price</dt>
        <dd className="text-sm font-medium text-red-500 col-span-2">{formatOffer(engineOffer)}</dd>
      </div>
      <div className="py-2 grid grid-cols-3 gap-4">
        <dt className="text-sm font-medium text-red-500">Front End</dt>
        <dd className="text-sm font-medium text-red-500 col-span-2">{formatOffer(vfeOffer)}</dd>
      </div>
      {canRunLookup && (
        <div className="py-2 grid grid-cols-3 gap-4">
          <dt className="text-sm font-medium text-gray-500">Engines Worldwide</dt>
          <dd className="text-sm col-span-2">
            <ButtonInput
              label="Run Lookup"
              onClick={handleLookup}
              classes=""
              disabled={!vehicle.id}
              isLoading={enginesWorldwideRefresh.isLoading}
              size="sm"
            />
          </dd>
        </div>
      )}
    </div>
  );
}
